import React from 'react'
import './Icon.css'

export type IconSize = 'compact' | 'standard' | 'featured' | 'large'

export interface IconProps {
  children: React.ReactNode
  size?: IconSize
  color?: string
  ariaLabel?: string
  className?: string
}

/**
 * Icon Component
 *
 * Wrapper for icons with consistent sizing and styling
 *
 * Sizes:
 * - compact: 20px (for tight spaces)
 * - standard: 24px (default, toolbar icons)
 * - featured: 32px (emphasized icons)
 * - large: 48px (brick type cards)
 *
 * Usage:
 * - Wrap lucide-react SVG icons or legacy emoji icons
 * - Provides consistent sizing across UI
 * - Inherits color from parent or uses custom color
 * - Supports both React elements (lucide-react) and strings (emoji)
 *
 * @component
 */
export const Icon: React.FC<IconProps> = ({
  children,
  size = 'standard',
  color,
  ariaLabel,
  className = '',
}) => {
  const classNames = ['icon', `icon--${size}`, className]
    .filter(Boolean)
    .join(' ')

  const style: React.CSSProperties = color ? { color } : {}

  return (
    <span
      className={classNames}
      style={style}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
    >
      {children}
    </span>
  )
}
